import React, { Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { CircularProgress, Box } from "@mui/material";
import { AppLayout } from "./components/layout/AppLayout.js";

const DashboardPage = React.lazy(() => import("./pages/DashboardPage.js"));
const SubscriptionsPage = React.lazy(() => import("./pages/SubscriptionsPage.js"));
const SubscriptionDetailPage = React.lazy(() => import("./pages/SubscriptionDetailPage.js"));
const RenewalsPage = React.lazy(() => import("./pages/RenewalsPage.js"));
const PlansPage = React.lazy(() => import("./pages/PlansPage.js"));
const PlanEditorPage = React.lazy(() => import("./pages/PlanEditorPage.js"));
// Funnel-Bereich: 1 Landing + 4 dedizierte Pflege-Seiten + 1 Datenqualität.
const FunnelOverviewPage = React.lazy(() => import("./pages/funnel/FunnelOverviewPage.js"));
const FunnelQuizPage = React.lazy(() => import("./pages/funnel/FunnelQuizPage.js"));
const FunnelTextsPage = React.lazy(() => import("./pages/funnel/FunnelTextsPage.js"));
const FunnelCategoriesPage = React.lazy(() => import("./pages/funnel/FunnelCategoriesPage.js"));
const ProductsPage = React.lazy(() => import("./pages/ProductsPage.js"));
const CatalogTagsPage = React.lazy(() => import("./pages/CatalogTagsPage.js"));
const FunnelHealthPage = React.lazy(() => import("./pages/FunnelHealthPage.js"));
const SparepartsOverviewPage = React.lazy(
  () => import("./pages/spareparts/SparepartsOverviewPage.js"),
);
const SparepartsPartTypesPage = React.lazy(
  () => import("./pages/spareparts/SparepartsPartTypesPage.js"),
);
const OrdersPage = React.lazy(() => import("./pages/OrdersPage.js"));
const ReportingPage = React.lazy(() => import("./pages/ReportingPage.js"));
const GdprPage = React.lazy(() => import("./pages/GdprPage.js"));
const AuditLogPage = React.lazy(() => import("./pages/AuditLogPage.js"));
const SettingsPage = React.lazy(() => import("./pages/SettingsPage.js"));
const ConfigPage = React.lazy(() => import("./pages/ConfigPage.js"));
const NotificationsPage = React.lazy(() => import("./pages/NotificationsPage.js"));
const HelpPage = React.lazy(() => import("./pages/HelpPage.js"));
// Phase 10.5 — neue dedizierte Pages für Storefront-Widgets + Plan-Defaults
const PdpWidgetPage = React.lazy(() => import("./pages/storefront/PdpWidgetPage.js"));
const CustomerPortalPage = React.lazy(() => import("./pages/storefront/CustomerPortalPage.js"));
const PlanDefaultsPage = React.lazy(() => import("./pages/settings/PlanDefaultsPage.js"));

const LoadingFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
    <CircularProgress />
  </Box>
);

export function App() {
  return (
    <AppLayout>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />

          {/* Abo-Geschäft */}
          <Route path="/subscriptions" element={<SubscriptionsPage />} />
          <Route path="/subscriptions/renewals" element={<RenewalsPage />} />
          <Route path="/subscriptions/:id" element={<SubscriptionDetailPage />} />
          <Route path="/plans" element={<PlansPage />} />
          <Route path="/plans/new" element={<PlanEditorPage />} />
          <Route path="/plans/:id/edit" element={<PlanEditorPage />} />
          <Route path="/orders" element={<OrdersPage />} />

          {/* Funnel */}
          <Route path="/funnel" element={<FunnelOverviewPage />} />
          <Route path="/funnel/quiz" element={<FunnelQuizPage />} />
          <Route path="/funnel/product-lines" element={<ProductsPage />} />
          <Route path="/funnel/texts" element={<FunnelTextsPage />} />
          <Route path="/funnel/categories" element={<FunnelCategoriesPage />} />
          <Route path="/funnel/health" element={<FunnelHealthPage />} />

          {/* Katalog (Tag-Zuweisung + Taxonomie zusammengelegt) */}
          <Route path="/catalog/tags" element={<CatalogTagsPage />} />

          {/* Ersatzteile */}
          <Route path="/spareparts" element={<SparepartsOverviewPage />} />
          <Route path="/spareparts/part-types" element={<SparepartsPartTypesPage />} />

          {/* Reporting / Compliance */}
          <Route path="/reporting" element={<ReportingPage />} />
          <Route path="/gdpr" element={<GdprPage />} />
          <Route path="/audit" element={<AuditLogPage />} />

          {/* Storefront-Widgets · Phase 10.5 */}
          <Route path="/storefront/pdp-widget" element={<PdpWidgetPage />} />
          <Route path="/storefront/customer-portal" element={<CustomerPortalPage />} />

          {/* System */}
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/plan-defaults" element={<PlanDefaultsPage />} />
          <Route path="/settings/config" element={<ConfigPage />} />
          <Route path="/settings/notifications" element={<NotificationsPage />} />
          <Route path="/help" element={<HelpPage />} />

          {/* Backward-compat Redirects — alte URLs bleiben funktional. */}
          <Route path="/products" element={<Navigate to="/funnel/product-lines" replace />} />
          <Route path="/funnel-health" element={<Navigate to="/funnel/health" replace />} />
          <Route path="/dispenser-tags" element={<Navigate to="/catalog/tags" replace />} />
          <Route path="/tag-taxonomy" element={<Navigate to="/catalog/tags" replace />} />
        </Routes>
      </Suspense>
    </AppLayout>
  );
}
